import React, { FormEventHandler } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
} from "reactstrap";
import { TicketData, TicketRelation, TicketStatus } from "./types";
import {
  formatDateTime,
  getPriorityMeta,
  getRatingLabel,
  getStatusLabel,
  statusBadgeColor,
} from "./utils";

const DetailItem = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="d-flex justify-content-between align-items-start gap-3 py-2 border-bottom">
    <span className="text-muted">{label}</span>
    <div className="text-end">{value}</div>
  </div>
);

type TicketDetailsSidebarProps = {
  ticket: TicketData;
  canUpdateTicket: boolean;
  canManageRelations: boolean;
  canUpdateOnlyStatus: boolean;
  canViewFeedback: boolean;
  canRateTicket: boolean;
  savingTicket: boolean;
  editSubject: string;
  editDescription: string;
  editStatus: TicketStatus;
  editPriority: number;
  availableStatusOptions: Array<{ value: TicketStatus; label: string }>;
  relations: TicketRelation[];
  onEditSubjectChange: (value: string) => void;
  onEditDescriptionChange: (value: string) => void;
  onEditStatusChange: (value: TicketStatus) => void;
  onEditPriorityChange: (value: number) => void;
  onOpenAddRelationModal: () => void;
  onRemoveRelation: (relationId: number) => void;
  onOpenRatingModal: () => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
};

const TicketDetailsSidebar = ({
  ticket,
  canUpdateTicket,
  canManageRelations,
  canUpdateOnlyStatus,
  canViewFeedback,
  canRateTicket,
  savingTicket,
  editSubject,
  editDescription,
  editStatus,
  editPriority,
  availableStatusOptions,
  relations,
  onEditSubjectChange,
  onEditDescriptionChange,
  onEditStatusChange,
  onEditPriorityChange,
  onOpenAddRelationModal,
  onRemoveRelation,
  onOpenRatingModal,
  onSubmit,
}: TicketDetailsSidebarProps) => {
  const statusLabel = getStatusLabel(ticket.status);
  const priorityMeta = getPriorityMeta(ticket.priority);

  return (
    <div className="d-flex flex-column gap-3">
      <Card>
        <CardBody>
          <h4 className="card-title mb-3">Ticket details</h4>
          <DetailItem label="Creator" value={ticket.creator?.name || "—"} />
          <DetailItem label="Assignee" value={ticket.assignee?.name || "Unassigned"} />
          <DetailItem
            label="Status"
            value={<Badge color={statusBadgeColor(ticket.status)}>{statusLabel}</Badge>}
          />
          <DetailItem
            label="Priority"
            value={<Badge color={priorityMeta.tone}>{priorityMeta.label}</Badge>}
          />
          <DetailItem
            label="Category"
            value={ticket.category ? (
              <Badge style={{ backgroundColor: ticket.category.color, color: "#fff" }}>
                {ticket.category.name}
              </Badge>
            ) : "—"}
          />
          <DetailItem label="Created" value={formatDateTime(ticket.created_at)} />
          <DetailItem label="Updated" value={formatDateTime(ticket.updated_at)} />
          <DetailItem label="Closed" value={formatDateTime(ticket.closed_at)} />
          <DetailItem label="Resolution time" value={ticket.resolution_time_human || "—"} />
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h5 className="card-title mb-3">SLA & timing</h5>
          <div className="d-grid gap-2">
            {!ticket.first_response_at && ticket.first_response_due_at && (
              <Alert color={ticket.sla_first_response_breached ? "danger" : "warning"} fade={false} className="mb-0">
                {ticket.sla_first_response_breached
                  ? "First response SLA breached"
                  : `First response due ${formatDateTime(ticket.first_response_due_at)}`}
              </Alert>
            )}
            {ticket.first_response_at && (
              <Alert color="success" fade={false} className="mb-0">
                First response at {formatDateTime(ticket.first_response_at)}
              </Alert>
            )}
            {ticket.resolution_due_at && (
              <Alert color={ticket.sla_resolution_breached ? "danger" : "info"} fade={false} className="mb-0">
                {ticket.sla_resolution_breached
                  ? "Resolution SLA breached"
                  : `Resolution due ${formatDateTime(ticket.resolution_due_at)}`}
              </Alert>
            )}
            {ticket.escalated_at && (
              <Alert color="danger" fade={false} className="mb-0">
                Escalated at {formatDateTime(ticket.escalated_at)}
              </Alert>
            )}
          </div>
        </CardBody>
      </Card>

      {(relations.length > 0 || canManageRelations) && (
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title mb-0">Related tickets</h5>
              {canManageRelations && (
                <Button size="sm" color="light" onClick={onOpenAddRelationModal}>
                  Link
                </Button>
              )}
            </div>
            {relations.length > 0 ? (
              <div className="list-group list-group-flush">
                {relations.map((relation) => (
                  <div key={relation.id} className="list-group-item px-0">
                    <div className="d-flex justify-content-between gap-3">
                      <div>
                        <div className="d-flex flex-wrap gap-2 mb-1">
                          <Badge color="light" className="text-dark border">
                            {relation.relation_type}
                          </Badge>
                          <Badge color={statusBadgeColor((relation.related_ticket?.status as TicketStatus) || "open")}>
                            {getStatusLabel((relation.related_ticket?.status as TicketStatus) || "open")}
                          </Badge>
                        </div>
                        <Link to={`/tickets/${relation.related_ticket?.uuid}`} className="fw-semibold">
                          #{relation.related_ticket?.id} {relation.related_ticket?.subject || "Related ticket"}
                        </Link>
                      </div>
                      {canManageRelations && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger p-0"
                          onClick={() => onRemoveRelation(relation.id)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <small className="text-muted">No related tickets yet.</small>
            )}
          </CardBody>
        </Card>
      )}

      {canViewFeedback && (
        <Card>
          <CardBody>
            <h5 className="card-title mb-3">Feedback</h5>
            {ticket.satisfaction_rating ? (
              <div className="bg-light rounded p-3">
                <div className="h4 text-warning mb-2">
                  {"★".repeat(ticket.satisfaction_rating.rating)}
                  {"☆".repeat(5 - ticket.satisfaction_rating.rating)}
                </div>
                <div className="fw-semibold mb-2">
                  {getRatingLabel(ticket.satisfaction_rating.rating)} ({ticket.satisfaction_rating.rating}/5)
                </div>
                <div className="text-muted">
                  {ticket.satisfaction_rating.comment || "No written comment was provided."}
                </div>
              </div>
            ) : (
              <div className="d-flex justify-content-between align-items-center gap-3 bg-light rounded p-3">
                <div className="text-muted">Ticket is complete and ready for requester feedback.</div>
                <Button color="warning" onClick={onOpenRatingModal}>
                  Rate ticket
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody>
          <h5 className="card-title mb-3">{canUpdateTicket ? "Update ticket" : "Access"}</h5>
          {canUpdateTicket ? (
            <Form onSubmit={onSubmit}>
              <Row className="g-3">
                {canUpdateOnlyStatus && (
                  <Col md={12}>
                    <Alert color="light" fade={false} className="mb-0">
                      You can edit only the ticket status because you are the assignee.
                    </Alert>
                  </Col>
                )}
                <Col md={12}>
                  <Label className="form-label">Subject *</Label>
                  <Input
                    value={editSubject}
                    onChange={(event) => onEditSubjectChange(event.target.value)}
                    required
                    minLength={3}
                    maxLength={255}
                    disabled={canUpdateOnlyStatus}
                  />
                </Col>
                <Col md={6}>
                  <Label className="form-label">Status</Label>
                  <Input
                    type="select"
                    value={editStatus}
                    onChange={(event) => onEditStatusChange(event.target.value as TicketStatus)}
                  >
                    {availableStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={6}>
                  <Label className="form-label">Priority</Label>
                  <Input
                    type="select"
                    value={editPriority}
                    onChange={(event) => onEditPriorityChange(Number(event.target.value))}
                    disabled={canUpdateOnlyStatus}
                  >
                    <option value={1}>Critical</option>
                    <option value={2}>High</option>
                    <option value={3}>Medium</option>
                    <option value={4}>Low</option>
                  </Input>
                </Col>
                <Col md={12}>
                  <Label className="form-label">Description *</Label>
                  <Input
                    type="textarea"
                    rows={6}
                    value={editDescription}
                    onChange={(event) => onEditDescriptionChange(event.target.value)}
                    required
                    minLength={3}
                    disabled={canUpdateOnlyStatus}
                  />
                </Col>
                <Col md={12} className="d-flex justify-content-end">
                  <Button type="submit" color="success" disabled={savingTicket}>
                    {savingTicket ? "Saving..." : "Save changes"}
                  </Button>
                </Col>
              </Row>
            </Form>
          ) : (
            <Alert color="light" fade={false} className="mb-0">
              Only the ticket creator can edit all fields. The current assignee can change status only.
            </Alert>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default TicketDetailsSidebar;
